/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountsApiService } from '../../modules/centralisedManagement/shared/http';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AuthStoreService,
    ConnectionDetailsService,
    DownloadService,
    KeyStoreService,
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    PGPService,
    UserService,
    UtilsService
} from '../../core/services';
import { BaseComponent } from '../../core/components';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../core/models';
import { ExternalUtilsService } from '../shared/external-utils-service';
import { KeyStoreEnum } from '../../core/enums';
import { LoginModel } from '../../modules/loadbalancer/shared';
import { Observable, Subject, Subscriber } from 'rxjs';
import { complexPasswordValidation, nameValidation } from '../../core/directives/validation';
import { takeUntil } from 'rxjs/operators';

// @ts-ignore
import help from '../../../assets/centralisedManagement/help/en-us.json';

@Component({
    selector: 'app-login',
    templateUrl: './activateAccount.component.html',
    styleUrls: ['../externalPageStyles.scss', './activateAccount.component.scss']
})

export class ActivateAccountComponent extends BaseComponent implements OnInit {
    /**
     * Store the current step.
     */
    public activeStep = 1;

    /**
     * Create account form.
     */
    public createAccountForm: FormGroup;

    /**
     * Filename for key.
     */
    public privateKeyName = 'lbUserPrivate.key';

    /**
     * Invited flag.
     * @private
     */
    public invited = false;

    /**
     * User Account ID from Route
     * @private
     */
    private accountId: string;

    /**
     * Account Activation Code from Route
     * @private
     */
    private accountActivationCode: string;

    /**
     * Store the temporary user and organisation keys.
     * @private
     */
    private tempKeys: any = {
        decryptedUserPrivateKey: null,
        decryptedOrgPrivateKey: null
    };

    /**
     * Store the new keys until after activation.
     * @private
     */
    private newKeyStore: any = {
        userPrivateKey: null,
        orgPrivateKey: null
    };

    /**
     * As we are not extending base we need reference to destroy.
     * @protected
     */
    protected destroyed$: Subject<boolean> = new Subject();

    constructor(
        private accountsApiService: AccountsApiService,
        private authStoreService: AuthStoreService,
        private connectionDetailsService: ConnectionDetailsService,
        private downloadService: DownloadService,
        private externalUtilsService: ExternalUtilsService,
        private formBuilder: FormBuilder,
        private keyStoreService: KeyStoreService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private pgpService: PGPService,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private utilsService: UtilsService,
    ) {
        super();

        this.setupFormGroups();
        this.setupFormSubscribers();
    }

    public ngOnInit(): void {
        this.subscribeToRouteParams();
        this.languageService.help = help;
    }

    /**
     * Move to the next step.
     */
    public nextStep(): void {
        this.activeStep++;
    }

    /**
     * Set private key as true in form.
     * @decryptedKey
     */
    public downloadKeyAndValidate(decryptedKey: { armor: () => BlobPart; }): void {
        this.downloadService.generateDownload(decryptedKey.armor(), this.privateKeyName);
        this.createAccountForm.get('step2.keyDownloaded').setValue(true);
    }

    /**
     * Confirm user's new password and proceed to download step.
     */
    public generateKeysForNextStep(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const userObject: any = this.formatFormData();
            const userData = {
                user: userObject.firstName + ' ' + userObject.lastName
            };

            const confirmUser = this.invited
                ? this.generateKeysForInvitedUser(userData, userObject)
                : this.generateNewUserKeysWithNewPassword(userData, userObject);

            confirmUser
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.keyStoreService.storeState = KeyStoreEnum.OPEN;
                        this.keyStoreService.userPrivateKey = this.newKeyStore.userPrivateKey;
                        this.keyStoreService.orgPrivateKey = this.newKeyStore.orgPrivateKey;

                        this.nextStep();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: any): void => {
                        this.logService.error('ActivateAccountComponent: generateKeysForNextStep ', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Create user account.
     */
    public activateAccount(): void {
        const userObject: any = this.createAccountForm.value.step1;
        userObject.orgPrivateKey = this.newKeyStore.orgPrivateKey;

        // Remove specific keys from form object.
        delete userObject.confirmPassword;
        delete userObject.activationPasscode;
        delete userObject.passcodeVerified;

        if (!this.invited) {
            delete userObject.orgPrivateKey;
        }

        // Submit
        this.accountsApiService.activate(this.accountId, this.accountActivationCode, userObject)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    const userDetails: LoginModel = this.formatUserDetails(data);

                    // Set the login type 'centralised or loadbalancer'
                    this.authStoreService.setAuthModule(userDetails, data);

                    // Set up user data
                    this.userService.login(userDetails, data);

                    this.navigateUserToFirstPage();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ActivateAccountComponent:activateAccount', error.message);
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.confirmActivateError(error);
                }
            });
    }

    /**
     * Show registration error in confirm modal.
     * @param error
     * @private
     */
    private confirmActivateError(error: any): void {
        const message: string = this.externalUtilsService.getMessageFromError(error);

        const confirmationObject = {
            title: 'activate_account_error',
            message,
            callBack: this.closeModalHelper.bind(this),
            options: { translateText: false, hideCancelButton: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Close modal helper.
     * @private
     */
    private closeModalHelper(): void {
        this.modalService.close('confirmation-box');
    }

    /**
     * Helper function for confirming invited user.
     * @param userData
     * @param userObject
     * @private
     */
    private generateKeysForInvitedUser(userData: any, userObject: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.generateNewUserKeysWithNewPassword(userData, userObject)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                        next: (): void => {
                            observer.next();
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('ActivateAccountComponent: confirmInvitedUser', error.message);
                            observer.error(error);
                            observer.complete();
                        }
                    }
                );
        });
    }

    /**
     * Generate new keys for the user with their new password.
     * @param userData
     * @param userObject
     * @private
     */
    private generateNewUserKeysWithNewPassword(userData: any, userObject: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.pgpService.generateNewUserKeys(userData, userObject.password)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: ({ armoredKeys: armoredUsersKeys, hashRemainder }: any): void => {
                        userObject.publicKey = armoredUsersKeys.publicKey;
                        userObject.privateKey = armoredUsersKeys.privateKey;
                        userObject.password = hashRemainder;

                        const userEncryptedPrivateKey = armoredUsersKeys.privateKey;
                        this.newKeyStore.userPrivateKey = userEncryptedPrivateKey;

                        observer.next(userEncryptedPrivateKey);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('ActivateAccountComponent: GenerateNewUserKeysWithNewPassword', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Format the user details.
     * @param data
     * @private
     */
    private formatUserDetails(data: { token: string; }): LoginModel {
        const decodedToken = this.utilsService.decodeJWT(data.token);

        return {
            email: decodedToken.email,
            password: this.createAccountForm.get('step1.password').value,
            applianceAddress: this.connectionDetailsService.getAddress(),
            appliancePort: this.connectionDetailsService.getPort()
        };
    }

    /**
     * Remove any unneeded properties from the user object.
     * @private
     */
    private formatFormData(): any {
        const userObject = this.createAccountForm.value.step1;
        delete userObject.confirmPassword;

        return userObject;
    }

    /**
     * Navigate the user into their new account.
     * @private
     */
    private navigateUserToFirstPage(): void {
        const cmFirstPage: 'cm/dashboard' | 'cm/onboard' = this.userService.getUser('organisationId') > 0
            ? 'cm/dashboard' : 'cm/onboard';
        this.router.navigate([cmFirstPage]).then();
    }

    /**
     * Setup page form groups.
     * @private
     */
    private setupFormGroups(): void {
        // Set up our forms
        this.createAccountForm = this.formBuilder.group({
            step1: this.formBuilder.group({
                firstName: [null, [Validators.required, Validators.maxLength(254), nameValidation]],
                lastName: [null, [Validators.required, Validators.maxLength(254), nameValidation]],
                password: [null, [Validators.required, complexPasswordValidation]],
                confirmPassword: [null, [Validators.required, complexPasswordValidation]]
            }),
            step2: this.formBuilder.group({
                keyDownloaded: [null, Validators.required]
            }),
        });
    }

    /**
     * Setup form subscribers
     * @private
     */
    private setupFormSubscribers(): void {
        const confirmPasswordInput: AbstractControl = this.createAccountForm.get('step1.confirmPassword');
        confirmPasswordInput.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: confirmPassword => {
                    // Don't use debounce here as it will cause the submit button to flash.
                    const password = this.createAccountForm.get('step1.password').value;
                    this.utilsService.validatePasswordWithConfirmPassword(password, confirmPassword, confirmPasswordInput);
                }
            });
    }

    /**
     * Subscribe to route params.
     * @private
     */
    private subscribeToRouteParams(): void {
        this.route.params
            .subscribe({
                next: params => {
                    this.accountId = params['accountId'];
                    this.accountActivationCode = params['activationCode'];
                    this.invited = !!params['invited'];
                }
            });
    }
}
